import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus';
import BoardUtility from '@/scripts/board-utility';
import { DocumentTemplateService } from '@/scripts/services/document/document-template-service';
import { LogLevel } from "@/scripts/logger";

export const DocumentTemplateBoardServiceMixin = {
    data() {
        return {
            deletingItems: [],
        };
    },

    computed: {
        isOrganizationContext() {
            return this.$store.getters.isOrganizationContext;
        },

        accountId() {
            return this.$store.state.context.account.id;
        },

        agencyId() {
            return this.$store.state.context.agency.id;
        },

        clientId() {
            return this.$store.state.context.client.id;
        },

        baseUrl() {
            return this.$store.getters.baseUrl;
        },
    },

    methods: {
        refreshEvent() {
            EventBus.$emit('document-template:update');
        },

        openDesigner({ id }) {
            this.$router.push(`${this.baseUrl}/Documents/Templates/${id}/Design`);
        },

        openPreview({ id }) {
            let route = this.$router.resolve({ path: `${this.baseUrl}/Document/Template/${id}/Preview` });
            window.open(route.href);
        },

        createDocument(item) {
            const widget = {
                name: 'document-widget',
                options:{
                    type: 'template',
                    documentTemplateId: item.id,
                }
            }
            EventBus.$emit('open-widget', widget);
        },

        addToMyDocumentTemplates({ id }) {
            DocumentTemplateService.addToMyDocumentTemplates(this.accountId, id)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'You\'ve successfully added the document template to My Templates.'
                    );
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'There was an error adding the document template.',
                        { error }
                    );
                })
        },

        duplicateItem({ id, template: { name } }) {
            const widget = {
                name: 'duplicate-widget',
                entityId: id,
                entityName: name,
                serviceName: 'DocumentTemplates',
                entityType: 'Document template',
                fieldLabel: 'Choose a name for the new document template',
                fieldPlaceholder: 'Template Name',
                replaceEntityField: 'Name',
                onSuccess: () => {
                    this.refreshEvent();
                },
            };

            EventBus.$emit('open-widget', widget);
        },

        editItem({id}) {
            const widget = {
                name: 'document-template-widget',
                options:{
                    documentTemplateId: id,
                    type: 'template',
                }
            };

            EventBus.$emit('open-widget', widget);
        },

        onPublish(item) {
            if (!item.isAutoPublished) {
                const widget = {
                    name: 'document-template-publish-widget',
                    options:{
                        documentTemplateId: item.id,
                    } 
                };
                EventBus.$emit('open-widget', widget);
            } else {
                this.$helpers.showFailNotification('This template is already set to Auto Publish', { level: LogLevel.INFO });
            }
        },

        onUnpublish({ id }) {
            DocumentTemplateService.unpublish(id)
                .then(() => {
                    this.$helpers.showSuccessNotification('Your publish settings have been updated.');
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred while saving your document publish settings.', { error });
                })
        },

        deleteItem(item) {

            this.deletingItems = [item];
            DocumentTemplateService.delete(item.id)
                .then(() => {
                    this.$helpers.showSuccessNotification('Document template deleted successfully.');
                    this.refreshEvent();
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('An error occurred trying to delete the document template.', { error });
                    this.deletingItems = [];
                });
        },

        // Popup

        showDeleteDocumentTemplateModal(template) {
            this.actions = {
                confirm: {
                    label: 'Delete',
                    action: () => {
                        this.deleteItem(template);
                    },
                },

                cancel: {
                    label: 'Cancel',
                },
            };

            const content = '<p>All information associated with the document template will be deleted. You can not undo this action.</p>';

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Are you sure you want to delete this document template?',
                    content,
                    actions: this.actions,
                    confirmText: 'Delete',
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                }
            );
        },

        // Table mapper

        documentTemplateItems(items) {
            return items.map((item) => {
                const mappedCategories = item.Categories
                ? item.Categories.map(({ Id, Category }) => ({
                    id: Id,
                    name: Category
                }))
                : [];

                const mapThumbnails = item.DocumentTemplateUrls.map((item) => item.PreviewImageUrl);

                const templateItem = {
                    id: item.Id,
                    template: {
                        id: item.Id,
                        name: item.Name,
                        previewImageUrl: mapThumbnails[0] || null,
                        previewThumbnailsUrl: mapThumbnails || [] ,
                        config: item.TemplateJson || null,
                    },
                    categories: mappedCategories,
                    
                    autoPublished: {
                        documentTemplateId: item.Id,
                        name: item.Name,
                        isAutoPublished: item.IsAutoPublished,
                        isLoading: false,
                    },
                    asset: {
                        documentTemplateId: item.Id,
                        isAutoPublished: item.IsAutoPublished,
                        published: item['SharedAssets@odata.count'],
                    },
                    isAutoPublished: item.IsAutoPublished,
                    published: item['SharedAssets@odata.count'],
                    createdAt: this.$moment.utc(item.CreatedAt).local(),
                    publishedAt: this.$moment.utc(item.PublishedAt).local(),
                    updatedAt: this.$moment.utc(item.UpdatedAt).local(),
                };
    
                if (item.Account) {
                    const { Agency } = item.Account;
                    templateItem.vendor = {
                        id: Agency.Id,
                        link: null,
                        name: Agency.Name,
                        logoUrl: Agency.LogoImageUrl,
                    }
                };
    
                return templateItem;
            })
        },

        // Query methods

        getFilterParam(options, type) {
            const queryFilters = [];

            if (options.search) {
                queryFilters.push(this.getSearchFilter(options.search));
            };

            if (type === 'shared') {
                queryFilters.push(`SharedAssets/any(a: a/AgencyClient/ClientId eq ${this.clientId})`);
                return queryFilters.length > 0 ? `${queryFilters.join(' and ')}` : '';
            };

            if (type === 'subscription' && options.promoId) {
                queryFilters.push(`SharedAssets/any(a: a/PromoId eq ${options.promoId})`);
            } else {
                queryFilters.push(`AccountId eq ${ this.accountId }`);
            };

            if (type === 'published') {
                const typeFilter = this.isOrganizationContext
                    ? 'SharedAssets/any()'
                    : 'SharedAssets/any(a: a/AgencyClient/IsAccepted eq true)';
                queryFilters.push(typeFilter);
            } else if (type === 'unpublished') {
                queryFilters.push('not SharedAssets/any()');
            };

            if (this.agencyId) {
                queryFilters.push(`Account/AgencyId eq ${ this.agencyId }`);
            };
            
            if(options.filters) {
                const viewQueryFilter = this.getViewQueryFilter(options.filters);
                if (viewQueryFilter) {
                    queryFilters.push(viewQueryFilter);
                }
            };

            return queryFilters.length > 0 ? `${queryFilters.join(' and ')}` : '';
        },

        getViewQueryFilter(filters) {
            if (filters.value.length > 0) {
                const viewQueryFilters = filters.value.map(this.mapFilter);

                if (filters.operation === 'Or') {
                    return `(${viewQueryFilters.join(' or ')})`;
                } else {
                    return `(${viewQueryFilters.join(' and ')})`;
                }
            }

            return null;
        },

        mapFilter(filter) {
            switch (filter.key) {
                case 'name':
                    return BoardUtility.getDocumentNameFilter(filter);
                case 'category':
                    return BoardUtility.getDocumentCategoryFilter(filter);
                default:
                    return null;
            }
        },

        getSearchFilter(search) {
            const encodedSearch = encodeURIComponent(search);

            return `(contains(Name, '${encodedSearch}'))`;
        },

        getPageParam(options) {
            return options.itemsPerPage && options.page ? `${options.itemsPerPage * (options.page - 1)}` : null;
        },

        getTopParam(options) {
            return options.itemsPerPage ? `${options.itemsPerPage}` : null;
        },

        getOrderParam(options) {
            return options.sort ? `${options.sort.property} ${options.sort.direction}` : null;
        },

        getSelectParam(options) {
            return options.select ? `${options.select}` : null;
        },

        getExpandParams(type) {
            const query = ['Categories','DocumentTemplateUrls($orderby=Sequence asc)'];

            if (type === 'shared') {
                query.push('Account($select=Id;$expand=Agency($select=LogoImageUrl,Name,Id))');
            } else if (type === 'published') {
                query.push('SharedAssets($count=true;$top=0)');
            };

            return query.length > 0 ? `${ query.join(',') }` : '';
        },

        createDocumentTemplateParamsFromBoardQuery(options, type) {
            return {
                filter: this.getFilterParam(options, type),
                skip: this.getPageParam(options),
                top: this.getTopParam(options),
                orderBy: this.getOrderParam(options),
                expand: this.getExpandParams(type),
                select: this.getSelectParam(options),
                count: true,
            };
        },

        // Get Templates

        dispatchGetDocumentTemplates({ options = {}, type = null }, signal) {
            const requestConfig = this.createDocumentTemplateParamsFromBoardQuery(options, type);

            return new Promise((resolve, reject) => {
                DocumentTemplateService.getAll(requestConfig, signal)
                    .then(({ data }) => resolve({
                            items: this.documentTemplateItems(data.value),
                            count: data['@odata.count'],
                    }))
                    .catch(reject);
            });
        },
    },
};